import seo from '@/core/data/landingSeo.json'

const L = seo.locales.ja

export default {
  hero: L.hero,
  startFree: "無料で始める",
  features: {
    altTemplate: "{title}機能のビジュアルデモ",
    items: {
      learnStyle: {
        title: "参考画像からスタイルを学ぶ",
        description:
          "お気に入りの参考画像を選び、その階調、色彩、空気感を分析・解明します。",
      },
      seeGaps: {
        title: "違いを見て理解する",
        description:
          "視覚化されたチャートと差分カードで、階調、色調、彩度の違いを直感的に把握します。",
      },
      aiAnalysis: {
        title: "理解してから調整する",
        description:
          "AIが画面上の根拠に基づいてスタイルの成因を解説し、次の調整方向を提案します。",
      },
      applyStyle: {
        title: "自分の写真にスタイルを応用",
        description:
          "お手持ちの写真の撮影条件を踏まえ、どのスタイル要素を移植すべきかを判断します。",
      },
    },
  },
  advantages: {
    title: "スタイル理解から創作へのステップ",
    items: {
      styleLearning: {
        title: "模倣ではなく、スタイルを学ぶ",
        description:
          "参考画像は単にコピーするためのテンプレートではありません。PhotoScopeは、画像に隠された階調、色彩、雰囲気のルールを分析し、美しさが生まれる仕組みの理解を助けます。",
      },
      gapVisualization: {
        title: "差を見るだけで終わらせない、読み解く可視化",
        description:
          "輝度分布や色調の傾向を直感的なチャートと構造化されたカードで表示。複雑な視覚的差異をひと目で把握し、レタッチの迷いを解消します。",
      },
      aiInsights: {
        title: "理由を理解し、次のステップへ",
        description:
          "AIが単に差異を指摘するだけでなく、その違いが作品の印象にどう影響するかを解説。写真美学に基づき、次の調整ステップを優先度順に提案します。",
      },
      styleAdaptation: {
        title: "個性を活かした応用力",
        description:
          "学んだ知識を自身の写真に落とし込みます。元の写真が持つ光や色調を考慮し、参考スタイルをどう最適に取り入れるかを判断。作品の個性を守りつつ質感を高めます。",
      },
    },
  },
  steps: {
    title: "スタイル理解から作品完成までの4ステップ",
    items: {
      upload: {
        title: "写真と参考画像のアップロード",
        description:
          "お手持ちの写真をアップロードし、構成や色調を学びたい参考画像を選択します。",
      },
      analyze: {
        title: "ビジュアル分析で差を読み解く",
        description:
          "2画面比較、直感的なチャート、スタイル差分カードにより、重要な違いや調整の優先度を可視化します。",
      },
      edit: {
        title: "ガイドを読み、実際にレタッチする",
        description:
          "AI分析を含む詳細なレタッチ編集ガイドを読み、お気に入りの編集ソフトでご自身の写真に調整アイデアを反映させます。",
      },
      review: {
        title: "編集後画像をアップロードし、振り返る",
        description:
          "編集後の写真をアップロードして次のステップへのアドバイスを受け取り（近日公開）、Before/After比較カードを作成して成果をシェアします。",
      },
    },
  },
  useCases: {
    title: "写真学習のあらゆる段階に向けて",
    items: {
      beginners: {
        title: "初心者向け",
        description: {
          0: "お気に入りの参考画像を選び、プロの階調や色調のルールを直感的に学びます。",
          1: "分かりやすいチャートと差分カードで、自分の写真との違いを明確に理解します。",
          2: "AIによる分かりやすいレタッチ解説から、撮影・現像の基礎用語を身につけます。",
        },
      },
      enthusiasts: {
        title: "写真好きの方へ",
        description: {
          0: "憧れのスタイルを深く分析し、その色彩、ディテール、空気感の源を解明します。",
          1: "ご自身の写真と参考スタイルとの重要な差異と、優先すべき調整ポイントを特定します。",
          2: "感覚的なレタッチを有理的な判断に変え、創作の効率を大幅に引き上げます。",
        },
      },
      aesthetic: {
        title: "美的感性を磨きたい方へ",
        description: {
          0: "参考画像を通して、さまざまなスタイルに潜むビジュアル秩序と感情表現を研究します。",
          1: "色傾向や明暗のバランスが、どのように写真の「空気感」を形作るかを探ります。",
          2: "感覚的な「美しさ」を、言語化・可視化されたシステム分析へと落とし込みます。",
        },
      },
      practical: {
        title: "実用派の撮影者へ",
        description: {
          0: "参考画像で明確なゴール（目標）を設定し、レタッチの迷走や試行錯誤を防ぎます。",
          1: "正確な分析結果に基づき、ハイライト制御や色温度など重要な調整を優先して行います。",
          2: "編集後の写真をアップロードしてBefore/Afterカードを生成し、レタッチ成果を復習します。",
        },
      },
    },
  },
  testimonials: {
    title: "ユーザーの声",
    items: {
      sarah: {
        name: "Sarah Chen",
        content:
          "以前は参考画像のどこが良いのか分かりませんでしたが、PhotoScopeが階調や色の違いを教えてくれたことで、どこから手をつければいいか見えてきました。",
      },
      michael: {
        name: "Michael Rodriguez",
        content:
          "参考画像をただコピーするのではなく、どの違いがスタイルの鍵かを教えてくれます。現像の優先順位がクリアになりました。",
      },
      emma: {
        name: "Emma Thompson",
        content:
          "抽象的な「雰囲気」をデータと解説で視覚化してくれるのが魅力です。比較するたびに、自分の表現したいものが明確になります。",
      },
    },
  },
  faq: L.faq,
  footer: {
    copyright: "© 2026 PhotoScope。無断転載を禁じます。",
    product: {
      title: "製品",
      photoscope: "PhotoScope",
      beforeAfter: "Before & After"
    },
    resources: {
      title: "リソース",
      pricing: "料金",
      blog: "ブログ"
    },
    company: {
      title: "会社",
      contact: "お問い合わせ",
      terms: "利用規約",
      privacy: "プライバシーポリシー"
    }
  },
  pricing: {
    billing: {
      monthly: "月額",
      yearly: "年額",
    },
    badge: {
      yearlyDiscount: "最大{percent}%割引",
    },
    card: {
      recommended: "おすすめ",
      currentPlan: "現在のプラン",
      perMonth: "/月",
      save: "{percentage}%節約",
      discountPercent: "{percentage}%",
      billedAnnually: "年額で請求、{price}/年",
    },
    button: {
      subscribe: "購読",
      buyOnce: "1回限りの購入",
      getStarted: "今すぐ始める",
    },
    tiers: {
      free: "無料",
      standard: "スタンダード",
      pro: "プロ",
    },
    section: {
      title: "プランを選ぶ",
      subtitle: "無料で始め、必要に応じてアップグレード。",
    },
    cta: "今すぐ始める",
  },
  blog: {
    section: {
      title: "最新の記事",
      subtitle: "写真解析を極めるための洞察とヒントを探索",
      viewAll: "すべての記事を表示",
    },
    categories: {
      Guides: "ガイド",
      Concepts: "コンセプト",
    },
    card: {
      publishedOn: "公開日",
    },
    cta: {
      title: "PhotoScope で自分の写真を分析する",
      button: "写真をアップロード",
      subtitle: "任意の参考画像と比較し、視覚的な違いを明確で実行可能な洞察に変換します。",
    },
  },
};
